import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { saveSession, homeForRole } from '../lib/auth';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const justVerified = location.state?.verified;
  const passwordReset = location.state?.passwordReset;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [warn,  setWarn]  = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setWarn('');
    setLoading(true);
    try {
      const stored = JSON.parse(localStorage.getItem('mfai_device_' + email.toLowerCase()) || 'null');
      const deviceToken = stored && stored.expires > Date.now() ? stored.token : null;

      const res = await apiFetch('/api/auth/login', { method: 'POST', body: { email, password, device_token: deviceToken } });
      if (res.requires_email_verify) {
        sessionStorage.setItem('pending_verify_user_id', res.user_id);
        sessionStorage.setItem('pending_verify_email', email);
        navigate('/verify-email', { state: { email, user_id: res.user_id } });
        return;
      }
      if (res.requires_2fa) {
        sessionStorage.setItem('pending_2fa_user_id', res.user_id);
        sessionStorage.setItem('pending_2fa_email', email.toLowerCase());
        navigate('/verify-2fa');
        return;
      }
      if (res.token) {
        saveSession(res.token, res.user);
        navigate(homeForRole(res.user?.role));
        return;
      }
      setError(res.message || 'Login failed. Please try again.');
    } catch (e) {
      if (e.warn) { setWarn(e.message); }
      else { setError(e.message || 'Network error. Please try again.'); }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logoRow}>
          <img src="https://www.masterfileai.com/hubfs/logo3.png" alt="MasterFileAI" style={{ height: 36, maxWidth: 200, objectFit: "contain" }}
            onError={e => { e.target.style.display='none'; e.target.nextElementSibling.style.display='block'; }} />
          <span style={{ display:'none', color:'#0f172a', fontWeight:700, fontSize:20 }}>MasterFileAI</span>
        </div>
        <h1 style={styles.heading}>Sign in to your account</h1>
        {justVerified && <div style={styles.successBox}>✓ Email verified! Sign in to get started.</div>}
        {passwordReset && <div style={styles.successBox}>✓ Password updated! Sign in with your new password.</div>}
        {error && <div style={styles.errorBox}>{error}</div>}
        {warn  && <div style={styles.warnBox}>{warn}</div>}
        <form onSubmit={handleSubmit} style={styles.form}>
          <label style={styles.label}>Email</label>
          <input style={styles.input} type="email" placeholder="you@company.com" value={email} onChange={e => setEmail(e.target.value)} required autoFocus />
          <label style={styles.label}>Password</label>
          <input style={styles.input} type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} required />
          <button type="submit" style={styles.btn} disabled={loading}>{loading ? 'Signing in…' : 'Sign in'}</button>
        </form>
        <p style={styles.footerText}>
          <Link to="/forgot-password" style={styles.link}>Forgot password?</Link>{' · '}
          <Link to="/register" style={styles.link}>Create account</Link>
        </p>
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#f0f5fb', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px', fontFamily: "'Inter', system-ui, sans-serif" },
  card: { background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 16, padding: '48px 40px', width: '100%', maxWidth: 400, boxShadow: '0 4px 24px rgba(1,118,206,0.08)' },
  logoRow: { display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 32 },
  logoMark: { display: 'none' },
  logoText: { display: 'none' },
  heading: { color: '#0f172a', fontSize: 22, fontWeight: 700, margin: '0 0 24px', letterSpacing: '-0.3px' },
  errorBox: { background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 8, color: '#dc2626', fontSize: 13, padding: '10px 14px', marginBottom: 20 },
  form: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { color: '#64748b', fontSize: 12, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 10 },
  input: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, color: '#0f172a', fontSize: 14, padding: '11px 14px', outline: 'none', width: '100%', boxSizing: 'border-box' },
  btn: { marginTop: 20, background: '#F37428', border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 15, fontWeight: 600, padding: '13px', width: '100%' },
  footerText: { color: '#94a3b8', fontSize: 13, textAlign: 'center', marginTop: 24 },
  link: { color: '#0176CE', textDecoration: 'none', fontWeight: 500 },
  successBox: { background: 'rgba(5,150,105,0.08)', border: '1px solid rgba(5,150,105,0.4)', borderRadius: 8, color: '#059669', fontSize: 13, padding: '10px 14px', marginBottom: 20, textAlign: 'center' },
  warnBox: { background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.5)', borderRadius: 8, color: '#b45309', fontSize: 13, padding: '10px 14px', marginBottom: 20, fontWeight: 600 },
};
